import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../ui/gzToggle';
import { Feature, FeatureFlag } from '@nirvana/shared/constants';
import { AddonFieldComponents, AddonFieldComponentsMap } from '../types/AddonFieldComponentsMap';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzHardCodedFormField } from '../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { MonthlyProductType } from '../../../types/MonthlyProductType';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { ProductTypeInformation } from '../../../types/ProductTypeInformation';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../ui/gzRadio';
import { isHidden, toggleVisibility } from '../../../../utils/htmlElement.utils';
import { translateFn } from '../../../../utils/translator.helper';

export class LicenseMonthlyAddonsFormView extends BaseWebComponentView {
  public formGroup: FormGroup = new FormGroup({});
  public addonFields: AddonFieldComponentsMap;
  public atsGroupAddonFields: Set<string>;
  public xdrGroupAddonFields: Set<string>;
  public allowedFeaturesToDelegateWithoutOwning: Set<string>;
  public imES7daysDummyFlag = 'imES7daysStorage';
  public edrRetentionNoRetentionDummyFlag = 'edrRetentionNoRetention';

  public dom: {
    $advancedThreatSecurityToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
    $extendedDetectionAndResponseToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
    $endpointDetectionAndResponseText: GzTextWebComponent;
    $edrRetentionAddons: GzRadioGroupWebComponent;
    $edrRetentionNoRetentionOption: GzRadioWebComponent;
    $edrRetention90DaysOption: GzRadioWebComponent;
    $edrRetention180DaysOption: GzRadioWebComponent;
    $edrRetention365DaysOption: GzRadioWebComponent;
    $ownUseXdrSensorsInfoSection: HTMLElement;
    $ownUseXdrSensorsInfo: ProductTypeInformation;
    $imRetentionAddons: GzRadioGroupWebComponent;
    $imRetention7DaysOption: GzRadioWebComponent;
    $imRetention90DaysOption: GzRadioWebComponent;
    $imRetention180DaysOption: GzRadioWebComponent;
    $imRetention365DaysOption: GzRadioWebComponent;
    $atsInTrialText: HTMLElement;
  };
  public featureFlagToAddonFieldMap: { [key in FeatureFlag]: AddonFieldComponents } | {} = {};

  getDomElements() {
    this.dom = {
      $advancedThreatSecurityToggle: this.shadow.querySelector('#additional-advanced-threat-security-form-field'),
      $extendedDetectionAndResponseToggle: this.shadow.querySelector(
        '#additional-extended-detection-and-response-form-field'
      ),
      $edrRetentionAddons: this.shadow.querySelector('#endpoint-detection-and-response-retention-addons'),
      $edrRetentionNoRetentionOption: this.shadow.querySelector('#endpoint-detection-and-response-no-retention-option'),
      $edrRetention90DaysOption: this.shadow.querySelector('#endpoint-detection-and-response-90-option'),
      $edrRetention180DaysOption: this.shadow.querySelector('#endpoint-detection-and-response-180-option'),
      $edrRetention365DaysOption: this.shadow.querySelector('#endpoint-detection-and-response-365-option'),
      $endpointDetectionAndResponseText: this.shadow.querySelector('#additional-edr-text'),
      $ownUseXdrSensorsInfoSection: this.shadow.querySelector('#own-use-xdr-sensors-details'),
      $ownUseXdrSensorsInfo: {
        $infoText: this.shadow.querySelector('#own-use-xdr-sensors-details-text'),
        $link: this.shadow.querySelector('#own-use-xdr-sensors-details-link'),
      },
      $imRetentionAddons: this.shadow.querySelector('#integrity-monitor-retention-addons'),
      $imRetention7DaysOption: this.shadow.querySelector('#im-retention-7-option'),
      $imRetention90DaysOption: this.shadow.querySelector('#im-retention-90-option'),
      $imRetention180DaysOption: this.shadow.querySelector('#im-retention-180-option'),
      $imRetention365DaysOption: this.shadow.querySelector('#im-retention-365-option'),
      $atsInTrialText: this.shadow.querySelector('#ats-trial-started-text'),
    };

    this.initAddonToggles();
    this.setTranslatedStrings();
  }

  private initAddonToggles(): void {
    this.addonFields = {
      securityForExchange: {
        key: 'securityForExchange',

        $uiToggle: this.shadow.querySelector('#additional-security-for-exchange-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_EXCHANGE_INSTALL_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_EXCHANGE_INSTALL,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      emailSecurity: {
        key: 'emailSecurity',

        $uiToggle: this.shadow.querySelector('#additional-email-security-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_EMAIL_SECURITY_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_EMAIL_SECURITY_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      mobileSecurity: {
        key: 'mobileSecurity',

        $uiToggle: this.shadow.querySelector('#additional-mobile-security-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_MOBILE_SECURITY_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_MOBILE_SECURITY_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      fullDiskEncryption: {
        key: 'fullDiskEncryption',

        $uiToggle: this.shadow.querySelector('#additional-full-disk-encryption-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_VOLUME_ENCRYPTION_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_VOLUME_ENCRYPTION_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      securityForVirtualizedEnvironments: {
        key: 'securityForVirtualizedEnvironments',

        $uiToggle: this.shadow.querySelector('#additional-security-for-virtualized-environments-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING_INSTALL_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING_INSTALL,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      containerSecurity: {
        key: 'containerSecurity',

        $uiToggle: this.shadow.querySelector('#additional-container-security-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      phasr: {
        key: 'phasr',

        $uiToggle: this.shadow.querySelector('#additional-phasr-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_PHASR_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_PHASR_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      easm: {
        key: 'easm',

        $uiToggle: this.shadow.querySelector('#additional-easm-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_EASM_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_EASM_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      complianceManager: {
        key: 'complianceManager',

        $uiToggle: this.shadow.querySelector('#additional-compliance-manager-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_COMPLIANCE_PREMIUM_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_COMPLIANCE_PREMIUM_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      patchManagement: {
        key: 'patchManagement',

        $uiToggle: this.shadow.querySelector('#additional-patch-management-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      hyperDetect: {
        key: 'hyperDetect',

        $uiToggle: this.shadow.querySelector('#additional-hyper-detect-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_ADVANCED_THREAT_PREVENTION_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_ADVANCED_THREAT_PREVENTION_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      sandboxAnalyzer: {
        key: 'sandboxAnalyzer',

        $uiToggle: this.shadow.querySelector('#additional-sandbox-analyzer-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_SANDBOX_ANALYZER_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_SANDBOX_ANALYZER_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      endpointDetectionAndResponse: {
        key: 'endpointDetectionAndResponse',

        $uiToggle: this.shadow.querySelector('#additional-endpoint-detection-and-response-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_EDR_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_EDR_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],

        inTrialLabel: this.shadow.querySelector('#edr-trial-started-text'),
      },
      identityProviders: {
        key: 'identityProviders',

        $uiToggle: this.shadow.querySelector('#additional-identity-providers-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_IDENTITY_SENSORS_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_IDENTITY_SENSORS,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      productivityApps: {
        key: 'productivityApps',

        $uiToggle: this.shadow.querySelector('#additional-productivity-apps-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_PRODUCTIVITY_SENSORS_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_PRODUCTIVITY_SENSORS,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      networkSensors: {
        key: 'networkSensors',

        $uiToggle: this.shadow.querySelector('#additional-network-sensors-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_NETWORK_SENSORS_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_NETWORK_SENSORS,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      cloudWorkloads: {
        key: 'cloudWorkloads',

        $uiToggle: this.shadow.querySelector('#additional-cloud-workloads-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_CLOUD_SENSORS_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_CLOUD_SENSORS,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
      integrityMonitor: {
        key: 'integrityMonitor',

        $uiToggle: this.shadow.querySelector('#additional-integrity-monitor-form-field'),

        memoryToggle: new GzHardCodedFormField<boolean>(false),

        delegateFeature: Feature.PRODUCT_FEATURE_INTEGRITY_MONITOR_USABLE_DELEGATE,
        usableFeature: Feature.PRODUCT_FEATURE_INTEGRITY_MONITOR_USABLE,

        availableForProducts: [MonthlyProductType.EndpointSecurity],
      },
    };
  }

  setTranslatedStrings() {
    this.addonFields.securityForExchange.$uiToggle.Input.textContent =
      this.dependencies.translateFn('Security for Exchange');

    this.dom.$extendedDetectionAndResponseToggle.Input.textContent = 'eXtended Detection and Response';

    this.addonFields.emailSecurity.$uiToggle.Input.textContent = this.dependencies.translateFn('Email Security');

    this.addonFields.mobileSecurity.$uiToggle.Input.textContent = this.dependencies.translateFn('Mobile Security');

    this.addonFields.fullDiskEncryption.$uiToggle.Input.textContent =
      this.dependencies.translateFn('Full Disk Encryption');

    this.addonFields.securityForVirtualizedEnvironments.$uiToggle.Input.textContent = this.dependencies.translateFn(
      'Security For Virtualized Environments'
    );

    this.addonFields.containerSecurity.$uiToggle.Input.textContent =
      this.dependencies.translateFn('Container Protection');

    this.addonFields.phasr.$uiToggle.Input.textContent = this.dependencies.translateFn('PHASR');

    this.addonFields.easm.$uiToggle.Input.textContent = this.dependencies.translateFn('EASM');

    this.addonFields.complianceManager.$uiToggle.Input.textContent =
      this.dependencies.translateFn('Compliance Manager');

    this.addonFields.patchManagement.$uiToggle.Input.textContent = this.dependencies.translateFn('Patch Management');

    // ATS group
    this.dom.$advancedThreatSecurityToggle.Input.textContent =
      this.dependencies.translateFn('Advanced Threat Security');
    this.addonFields.hyperDetect.$uiToggle.Input.textContent = this.dependencies.translateFn('HyperDetect');
    this.addonFields.sandboxAnalyzer.$uiToggle.Input.textContent = this.dependencies.translateFn('Sandbox Analyzer');
    this.dom.$atsInTrialText.textContent = this.dependencies.translateFn('Trial started');

    // EDR has a tooltip
    this.addonFields.endpointDetectionAndResponse.$uiToggle.Input.textContent = this.dependencies.translateFn(
      'Endpoint Detection and Response'
    );

    this.dom.$edrRetentionNoRetentionOption.label = translateFn('No retention');
    this.dom.$edrRetention90DaysOption.label = translateFn('Data retention 90 days');
    this.dom.$edrRetention180DaysOption.label = translateFn('Data retention 180 days');
    this.dom.$edrRetention365DaysOption.label = translateFn('Data retention 1 year');

    this.addonFields.identityProviders.$uiToggle.Input.innerHTML = this.dependencies.translateFn('Identity Providers');
    this.addonFields.productivityApps.$uiToggle.Input.innerHTML = this.dependencies.translateFn('Productivity apps');
    this.addonFields.networkSensors.$uiToggle.Input.textContent = this.dependencies.translateFn('Network');

    this.addonFields.cloudWorkloads.$uiToggle.Input.innerHTML = this.dependencies.translateFn('Cloud workloads');

    this.dom.$endpointDetectionAndResponseText.innerHTML = this.dependencies.translateFn(
      'EDR requires HyperDetect and Sandbox Analyzer. These services will be enabled as well.'
    );

    this.addonFields.integrityMonitor.$uiToggle.Input.textContent =
      this.dependencies.translateFn('Integrity Monitoring');
    this.dom.$imRetention7DaysOption.label = this.dependencies.translateFn('Data retention 7 days (Default)');
    this.dom.$imRetention90DaysOption.label = this.dependencies.translateFn('Data retention 90 days');
    this.dom.$imRetention180DaysOption.label = this.dependencies.translateFn('Data retention 180 days');
    this.dom.$imRetention365DaysOption.label = this.dependencies.translateFn('Data retention 1 year');

    for (const key in this.addonFields) {
      if (this.addonFields[key]) {
        const addonField = this.addonFields[key] as AddonFieldComponents;
        if (addonField.inTrialLabel) {
          addonField.inTrialLabel.textContent = this.dependencies.translateFn('Trial started');
        }
      }
    }
  }

  buildSuccessView(data: unknown) {
    this.atsGroupAddonFields = new Set();
    this.atsGroupAddonFields.add(this.addonFields.hyperDetect.key);
    this.atsGroupAddonFields.add(this.addonFields.sandboxAnalyzer.key);

    this.xdrGroupAddonFields = new Set();
    this.xdrGroupAddonFields.add(this.addonFields.identityProviders.key);
    this.xdrGroupAddonFields.add(this.addonFields.productivityApps.key);
    this.xdrGroupAddonFields.add(this.addonFields.cloudWorkloads.key);
    this.xdrGroupAddonFields.add(this.addonFields.networkSensors.key);

    Object.entries(this.addonFields).forEach(([key, value]) => {
      this.featureFlagToAddonFieldMap[Feature.getFeatureFlag(value.usableFeature)] = value;
      this.featureFlagToAddonFieldMap[Feature.getFeatureFlag(value.delegateFeature)] = value;
    });

    this.allowedFeaturesToDelegateWithoutOwning = new Set();
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.securityForVirtualizedEnvironments.key);
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.containerSecurity.key);
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.hyperDetect.key);
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.sandboxAnalyzer.key);
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.endpointDetectionAndResponse.key);
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.phasr.key);
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.easm.key);
    this.allowedFeaturesToDelegateWithoutOwning.add(this.addonFields.complianceManager.key);

    this.dom.$imRetention7DaysOption.value = this.imES7daysDummyFlag; // placeholder value (default retention), not a real flag
    this.dom.$imRetention90DaysOption.value = FeatureFlag.PRODUCT_FEATURE_IM_ES_90DAYS_STORAGE;
    this.dom.$imRetention180DaysOption.value = FeatureFlag.PRODUCT_FEATURE_IM_ES_180DAYS_STORAGE;
    this.dom.$imRetention365DaysOption.value = FeatureFlag.PRODUCT_FEATURE_IM_ES_365DAYS_STORAGE;

    this.dom.$edrRetentionNoRetentionOption.value = this.edrRetentionNoRetentionDummyFlag; // placeholder value (default retention), not a real flag
    this.dom.$edrRetention90DaysOption.value = FeatureFlag.PRODUCT_FEATURE_EDR_ES_90DAYS_STORAGE;
    this.dom.$edrRetention180DaysOption.value = FeatureFlag.PRODUCT_FEATURE_EDR_ES_180DAYS_STORAGE;
    this.dom.$edrRetention365DaysOption.value = FeatureFlag.PRODUCT_FEATURE_EDR_ES_365DAYS_STORAGE;
  }

  buildErrorView(error: Error) {
    return;
  }

  public hideFeatureAddons(features: FeatureFlag[]): void {
    // hide addons
    for (const feature of features) {
      if (this.featureFlagToAddonFieldMap[feature] && this.featureFlagToAddonFieldMap[feature].$uiToggle) {
        toggleVisibility(this.featureFlagToAddonFieldMap[feature].$uiToggle, false);
      }
    }

    // hide Advanced Threat Security if HyperDetect and Sandbox Analyzer are not displayed
    if (isHidden(this.addonFields.hyperDetect.$uiToggle) && isHidden(this.addonFields.sandboxAnalyzer.$uiToggle)) {
      toggleVisibility(this.dom.$advancedThreatSecurityToggle, false);
    }

    // hide EDR text if addons is not displayed
    if (isHidden(this.addonFields.endpointDetectionAndResponse.$uiToggle)) {
      toggleVisibility(this.shadow.querySelector('#additional-edr-text'), false);
    }
  }

  public setCheckedValueForFeatureAddons(features: FeatureFlag[], checkedValue: boolean): void {
    for (const feature of features) {
      if (this.featureFlagToAddonFieldMap[feature]?.$uiToggle) {
        this.featureFlagToAddonFieldMap[feature].$uiToggle.value = checkedValue;
      }
    }
  }
}
